import type { Locale } from "../i18n";

interface HomeContent {
  readonly heading: string;
  readonly introduction: string;
  readonly explore: string;
  readonly spaces: string;
  readonly spaceNames: readonly [string, string, string, string];
  readonly concept: string;
  readonly inspirationTitle: string;
  readonly inspirationBody: string;
  readonly all: string;
  readonly empty: string;
  readonly more: string;
  readonly skip: string;
  readonly menu: string;
  readonly close: string;
  readonly studioTitle: string;
  readonly studioBody: string;
  readonly preview: string;
  readonly invitation: string;
  readonly invitationBody: string;
  readonly source: string;
  readonly serviceError: string;
}

export const homeContent: Readonly<Record<Locale, HomeContent>> = {
  es: {
    heading: "Tu espacio.\nTu próxima etapa.",
    introduction: "Interiores para tiendas, oficinas y hogares en Tailandia. Encuentra tu inspiración y consulta el diseño y el alcance con un equipo de proyecto.",
    explore: "Explorar espacios", spaces: "¿Qué espacio estás planificando?",
    spaceNames: ["Tiendas y restaurantes", "Oficinas", "Condominios", "Casas"],
    concept: "Imagen conceptual · No es una obra terminada",
    inspirationTitle: "Ideas para tu próximo espacio", inspirationBody: "Fotografías de referencia y conceptos de IA para preparar tus necesidades. No representan obras terminadas de un contratista.",
    all: "Todos", empty: "Todavía no hay imágenes para este tipo de espacio.", more: "Ver más ideas", skip: "Saltar al contenido", menu: "Abrir menú", close: "Cerrar menú",
    studioTitle: "Imagina el espacio.\nAntes de construir.", studioBody: "Prepara un plano, explora la vista 3D y comparte tus ideas. Los diseños preliminares necesitan una visita al lugar y confirmación profesional.",
    preview: "Vista previa de la herramienta de diseño", invitation: "Un mejor espacio empieza con una conversación", invitationBody: "Cuéntanos el uso, los objetivos y el estilo de tu espacio. El presupuesto escrito y el alcance del contrato se preparan después de revisar el lugar.",
    source: "Fuente y licencia", serviceError: "No se pudieron cargar los estudios. Inténtalo más tarde.",
  },
  th: {
    heading: "พื้นที่ที่ใช่\nเริ่มจากไอเดียของคุณ",
    introduction: "เปลี่ยนร้านค้า ออฟฟิศ หรือบ้านในประเทศไทย ให้เป็นพื้นที่ที่ตอบโจทย์ เริ่มจากแรงบันดาลใจ แล้วคุยเรื่องแบบและขอบเขตงานกับทีมโครงการ",
    explore: "ค้นหาแรงบันดาลใจ", spaces: "คุณกำลังวางแผนพื้นที่แบบไหน?",
    spaceNames: ["ร้านค้าและร้านอาหาร", "สำนักงาน", "คอนโด", "บ้านและทาวน์เฮาส์"],
    concept: "ภาพแนวคิดการออกแบบ · ไม่ใช่ผลงานก่อสร้างที่เสร็จแล้ว",
    inspirationTitle: "ไอเดียสำหรับพื้นที่ถัดไปของคุณ",
    inspirationBody: "สำรวจภาพอ้างอิงและแนวคิด AI เพื่อเตรียมบรีฟ ภาพเหล่านี้ไม่ใช่หลักฐานผลงานของผู้รับเหมา",
    all: "ทั้งหมด", empty: "ยังไม่มีภาพอ้างอิงสำหรับพื้นที่ประเภทนี้", more: "ดูไอเดียเพิ่มเติม",
    skip: "ข้ามไปยังเนื้อหา", menu: "เปิดเมนู", close: "ปิดเมนู",
    studioTitle: "ลองวางพื้นที่\nก่อนเริ่มงานจริง",
    studioBody: "เตรียมผังห้อง สำรวจภาพ 3D และส่งไอเดียให้ทีมตรวจสอบ แบบเบื้องต้นยังต้องผ่านการสำรวจหน้างานและการยืนยันจากผู้เชี่ยวชาญ",
    preview: "ภาพตัวอย่างเครื่องมือออกแบบ", invitation: "เริ่มต้นด้วยการพูดคุย",
    invitationBody: "เล่าเรื่องพื้นที่ เป้าหมาย และสิ่งที่คุณต้องการ ก่อนสำรวจและจัดทำใบเสนอราคาเป็นลายลักษณ์อักษร",
    source: "ที่มาและใบอนุญาต", serviceError: "ยังโหลดรายชื่อสตูดิโอไม่ได้ กรุณาลองใหม่ภายหลัง",
  },
  ko: {
    heading: "당신의 공간,\n아이디어에서 시작합니다",
    introduction: "태국의 상가, 오피스, 집을 목적에 맞는 공간으로. 마음에 드는 공간을 찾고 프로젝트 팀과 디자인·공사 범위를 이야기하세요.",
    explore: "공간 아이디어 둘러보기", spaces: "어떤 공간을 계획하고 있나요?",
    spaceNames: ["상가·레스토랑", "오피스", "콘도", "주택·타운하우스"],
    concept: "디자인 콘셉트 이미지 · 완료된 시공 사례가 아닙니다",
    inspirationTitle: "다음 공간을 위한 영감",
    inspirationBody: "참고 사진과 AI 콘셉트로 원하는 방향을 구체화하세요. 업체의 실제 시공 실적을 증명하는 이미지가 아닙니다.",
    all: "전체", empty: "이 공간 유형의 참고 이미지가 아직 없습니다.", more: "아이디어 더 보기",
    skip: "본문으로 건너뛰기", menu: "메뉴 열기", close: "메뉴 닫기",
    studioTitle: "공사 전에,\n공간을 먼저 그려보세요",
    studioBody: "평면도와 3D 화면으로 아이디어를 정리해 팀에 전달하세요. 초기 설계는 현장 실측과 전문가 확인이 필요합니다.",
    preview: "설계 도구 미리보기 이미지", invitation: "좋은 공간의 시작은 대화입니다",
    invitationBody: "공간의 용도, 목표, 원하는 분위기를 알려주세요. 현장 확인 후 서면 견적과 계약 범위를 정리합니다.",
    source: "출처·라이선스", serviceError: "업체 목록을 불러오지 못했습니다. 잠시 후 다시 확인해 주세요.",
  },
  en: {
    heading: "Your space.\nYour next chapter.",
    introduction: "Thoughtful interiors for shops, offices and homes in Thailand. Find your direction, then discuss the design and scope with a project team.",
    explore: "Explore spaces", spaces: "What space are you planning?",
    spaceNames: ["Shops & restaurants", "Offices", "Condos", "Homes & townhouses"],
    concept: "Design concept image · Not a completed construction project",
    inspirationTitle: "Ideas for your next space",
    inspirationBody: "Reference photography and AI concepts to help shape your brief. These images do not represent a contractor’s completed work.",
    all: "All spaces", empty: "No reference images are available for this space type yet.", more: "Explore more ideas",
    skip: "Skip to content", menu: "Open menu", close: "Close menu",
    studioTitle: "Picture the space.\nBefore you build.",
    studioBody: "Prepare a floor plan, explore the 3D view and share your ideas for review. Preliminary designs need a site survey and professional confirmation.",
    preview: "Design tool preview image", invitation: "A better space starts with a conversation",
    invitationBody: "Tell us about your space, goals and design direction. Written quotations and contract scope follow a site review.",
    source: "Source & license", serviceError: "Studios could not be loaded. Please try again later.",
  },
  zh: {
    heading: "理想空间，\n从你的灵感开始",
    introduction: "为泰国的商铺、办公室与住宅寻找合适的设计。先探索灵感，再与项目团队讨论设计与施工范围。",
    explore: "探索空间灵感", spaces: "你正在规划什么空间？",
    spaceNames: ["商铺与餐厅", "办公室", "公寓", "住宅与联排别墅"],
    concept: "设计概念图 · 非已完成施工案例",
    inspirationTitle: "为下一个空间寻找灵感", inspirationBody: "参考照片与 AI 概念图帮助完善需求。这些图片不代表承包商的实际施工成果。",
    all: "全部", empty: "此空间类型暂无参考图片。", more: "查看更多灵感",
    skip: "跳至正文", menu: "打开菜单", close: "关闭菜单",
    studioTitle: "动工之前，\n先看见空间的可能",
    studioBody: "准备平面图、探索 3D 视图并提交想法供团队审核。初步设计仍需现场测量和专业确认。",
    preview: "设计工具预览图", invitation: "好空间，从沟通开始",
    invitationBody: "告诉我们空间用途、目标和设计方向。现场确认后再制定书面报价和合同范围。",
    source: "来源与许可", serviceError: "暂时无法加载工作室，请稍后重试。",
  },
  ja: {
    heading: "あなたの空間を、\nアイデアからかたちに",
    introduction: "タイの店舗、オフィス、住まいに合うインテリアを。イメージを探し、設計と施工範囲をプロジェクトチームと相談しましょう。",
    explore: "空間のアイデアを探す", spaces: "どんな空間を計画していますか？",
    spaceNames: ["店舗・レストラン", "オフィス", "コンドミニアム", "住宅・タウンハウス"],
    concept: "デザインコンセプト画像 · 完成した施工事例ではありません",
    inspirationTitle: "次の空間のためのヒント", inspirationBody: "参考写真と AI コンセプトで希望を具体化しましょう。施工会社の実績を示す画像ではありません。",
    all: "すべて", empty: "この空間タイプの参考画像はまだありません。", more: "アイデアをもっと見る",
    skip: "本文へ移動", menu: "メニューを開く", close: "メニューを閉じる",
    studioTitle: "工事の前に、\n空間を描いてみよう",
    studioBody: "間取りと 3D 画面でアイデアを整理して共有できます。初期設計には現地調査と専門家の確認が必要です。",
    preview: "設計ツールのプレビュー画像", invitation: "理想の空間は、対話から",
    invitationBody: "用途、目標、希望の雰囲気を教えてください。現場確認後に書面見積と契約範囲を整理します。",
    source: "出典・ライセンス", serviceError: "スタジオを読み込めませんでした。後ほどお試しください。",
  },
};
